import React, { useEffect, useMemo, useState } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    getProfile2faOption,
    setCabinetProfileTwoFactorModalStatus,
    setProfile2faGoogleCode,
    setProfile2faOption
} from "../../../store/cabinet/action";

import Stepper from "./components/Stepper";
import MethodStep from "./components/MethodStep";
import SetupStep from "./components/SetupStep";
import BackupCodesStep from "./components/BackupCodesStep";

import twoFaIcon from "../../../theme/assets/svg/b_cabinet_profile_2fa_icon.svg";

const TWO_FACTOR_STEPS = {
    METHOD: 1,
    SETUP: 2,
    BACKUP_CODES: 3,
};

const CabinetProfileTwoFactorModal = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const modalStatus = useSelector(store => store.cabinet.cabinetProfileTwoFactorModalStatus);
    const profile2faOption = useSelector(store => store.cabinet.profile2faOption);

    const profile2faSelectedOptionStarted = useSelector(store => store.cabinet.profile2faSelectedOptionStarted);
    const profile2faGoogleCodeStatusStarted = useSelector(store => store.cabinet.profile2faGoogleCodeStatusStarted);
    const profile2faGoogleCodeStatusErr = useSelector(store => store.cabinet.profile2faGoogleCodeStatusErr);

    const [step, setStep] = useState(TWO_FACTOR_STEPS.METHOD);
    const [selectedMethod, setSelectedMethod] = useState("");
    const [setupData, setSetupData] = useState(null);
    const [backupCodesData, setBackupCodesData] = useState(null);

    const isLoading =
        profile2faSelectedOptionStarted ||
        profile2faGoogleCodeStatusStarted;

    const availableProviders = useMemo(() => {
        return Array.isArray(profile2faOption?.available_providers)
            ? profile2faOption.available_providers
            : [];
    }, [profile2faOption]);

    const selectedProvider = useMemo(() => {
        return availableProviders.find((provider) => provider.value === selectedMethod) || null;
    }, [availableProviders, selectedMethod]);

    const closeModal = () => {
        if (isLoading) return;

        dispatch(setCabinetProfileTwoFactorModalStatus(false));
    };

    const successSetProfile2faOptionHandler = (response) => {
        if (!response) return;

        setSetupData(response);
        setStep(TWO_FACTOR_STEPS.SETUP);
    };

    const successSetProfile2faGoogleCodeHandler = (response) => {
        if (!response) return;

        setBackupCodesData(response);
        setStep(TWO_FACTOR_STEPS.BACKUP_CODES);
    };

    const continueHandler = () => {
        if (step === TWO_FACTOR_STEPS.METHOD) {
            if (!selectedProvider?.value) return;
            if (isLoading) return;

            const payload = {
                provider: selectedProvider.value,
            };

            dispatch(setProfile2faOption(payload, successSetProfile2faOptionHandler));

            return;
        }

        if (step === TWO_FACTOR_STEPS.SETUP) {
            setStep(TWO_FACTOR_STEPS.BACKUP_CODES);
        }
    };

    const backHandler = () => {
        if (isLoading) return;

        if (step === TWO_FACTOR_STEPS.METHOD) {
            closeModal();
            return;
        }

        setStep((prevStep) => prevStep - 1);
    };

    const verifyHandler = (code) => {
        if (isLoading) return;
        if (!setupData?.provider) return;

        const payload = {
            provider: setupData.provider,
            code,
        };

        dispatch(setProfile2faGoogleCode(payload, successSetProfile2faGoogleCodeHandler));
    };

    useEffect(() => {
        if (modalStatus) {
            dispatch(getProfile2faOption());
        }
    }, [modalStatus, dispatch]);

    useEffect(() => {
        if (!modalStatus) {
            setStep(TWO_FACTOR_STEPS.METHOD);
            setSelectedMethod("");
            setSetupData(null);
            setBackupCodesData(null);
            return;
        }

        if (!availableProviders.length) return;

        const preferredProvider =
            profile2faOption?.pending_provider ||
            profile2faOption?.provider ||
            availableProviders[0]?.value ||
            "";

        const isPreferredAvailable = availableProviders.some((provider) => {
            return provider.value === preferredProvider;
        });

        setSelectedMethod(isPreferredAvailable ? preferredProvider : availableProviders[0].value);
    }, [modalStatus, availableProviders, profile2faOption]);

    return (
        <Popup
            open={!!modalStatus}
            modal
            nested
            closeOnDocumentClick={!isLoading}
            onClose={closeModal}
            className="cabinetProfileTwoFactorModal"
        >
            <div className={Styles.modalContainer}>
                <div
                    className={`
                        ${Styles.modalCloseBtn}
                        ${isLoading ? Styles.modalCloseBtnDisabled : ""}
                    `}
                    onClick={closeModal}
                >
                    <div />
                    <div />
                </div>

                <div className={Styles.modalContentWrap}>
                    <div
                        className={`
                            ${Styles.titleRow}
                            ${step !== TWO_FACTOR_STEPS.METHOD ? Styles.titleRowNoIcon : ""}
                        `}
                    >
                        {step === TWO_FACTOR_STEPS.METHOD && (
                            <div className={Styles.titleIcon}>
                                <img src={twoFaIcon} alt="2fa" />
                            </div>
                        )}

                        <div className={Styles.titleTextWrap}>
                            <div className={Styles.title}>
                                {step === TWO_FACTOR_STEPS.METHOD && t("Двухфакторная аутентификация")}
                                {step === TWO_FACTOR_STEPS.SETUP && t("Настройте приложение-аутентификатор")}
                                {step === TWO_FACTOR_STEPS.BACKUP_CODES && t("Сохраните резервные коды")}
                            </div>

                            <div className={Styles.note}>
                                {step === TWO_FACTOR_STEPS.METHOD && t("Безопасность аккаунта")}
                                {step === TWO_FACTOR_STEPS.SETUP && t("Отсканируйте QR-код в Google Authenticator и введите 6-значный код для проверки.")}
                                {step === TWO_FACTOR_STEPS.BACKUP_CODES && t("Двухфакторная аутентификация включена. Сохраните эти резервные коды в безопасном месте.")}
                            </div>
                        </div>
                    </div>

                    <Stepper currentStep={step} />

                    {step === TWO_FACTOR_STEPS.METHOD && (
                        <MethodStep
                            providers={availableProviders}
                            selectedMethod={selectedMethod}
                            onSelectMethod={setSelectedMethod}
                            onCancel={closeModal}
                            onContinue={continueHandler}
                            isLoading={profile2faSelectedOptionStarted}
                        />
                    )}

                    {step === TWO_FACTOR_STEPS.SETUP && (
                        <SetupStep
                            setupData={setupData}
                            onBack={backHandler}
                            onVerify={verifyHandler}
                            isLoading={profile2faGoogleCodeStatusStarted}
                            formErr={profile2faGoogleCodeStatusErr}
                        />
                    )}

                    {step === TWO_FACTOR_STEPS.BACKUP_CODES && (
                        <BackupCodesStep
                            backupCodesData={backupCodesData}
                            onBack={backHandler}
                            onDone={closeModal}
                        />
                    )}
                </div>
            </div>
        </Popup>
    );
};

export default CabinetProfileTwoFactorModal;